import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, BookOpen } from "lucide-react";
import { api, participant, formatApiErrorDetail } from "../lib/api";
import { LikertRow } from "../components/LikertRow";
import { ReferencesModal } from "../components/ReferencesModal";

const DISCLOSURE = `Copyright © 2026 SAA ANALYTICS. All rights reserved.
All content on this website, including text, graphics, logos, and code, is the property of SAA ANALYTICS and is protected by international copyright laws. No part of this site may be reproduced, distributed, or transmitted in any form or by any means without the prior written permission of the owner.

This assessment was developed by SAA Analytics based on well-established theories and empirical research in the provided references.
While these scientific models informed the selection of the competency domains measured, all assessment items were independently written by SAA Analytics and are original in their wording and structure. Accordingly, this assessment is not a copy, reproduction, or literal adaptation of any existing copyrighted scale or questionnaire. It is an original instrument inspired by the academic literature and designed specifically to measure the competencies relevant to the objectives of the SAA Analytics research program.`;

export default function TestRunner() {
  const { testId, phase } = useParams();
  const nav = useNavigate();
  const [scale, setScale] = useState({});
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [showRefs, setShowRefs] = useState(false);
  const p = participant.get();

  useEffect(() => {
    if (!p) {
      nav("/");
      return;
    }
    setAcknowledged(false);
    setAnswers({});
    Promise.all([
      api.get("/config/tests"),
      api.get(`/participant/${p.code}/progress`),
    ]).then(([{ data }, { data: prog }]) => {
      setScale(data.scale);
      const t = data.tests.find((x) => x.id === testId);
      if (!t) {
        toast.error("Test not found");
        nav("/dashboard");
        return;
      }
      const doneList = prog[phase] || [];
      if (doneList.includes(testId)) {
        toast.error("This test has already been submitted and cannot be changed.");
        nav("/dashboard");
        return;
      }
      setTest(t);
    });
    // eslint-disable-next-line
  }, [testId]);

  const answered = Object.keys(answers).length;
  const total = test?.questions.length || 0;
  const pct = total ? Math.round((answered / total) * 100) : 0;

  const setAnswer = (n, v) => setAnswers((a) => ({ ...a, [n]: v }));

  const submit = async () => {
    if (answered < total) {
      toast.error(`Please answer all ${total} questions (${total - answered} remaining).`);
      const firstUn = test.questions.find((q) => !answers[q.n]);
      if (firstUn) document.querySelector(`[data-testid="question-${firstUn.n}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/participant/${p.code}/submit`, { test_id: testId, phase, answers });
      toast.success("Responses saved.");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!test) return <div className="p-12 text-[#1E293B]/60">Loading…</div>;

  if (!acknowledged) {
    return (
      <div className="min-h-screen bg-[#F1F5F9]">
        <header className="sticky top-0 z-10 border-b border-[#E2E8F0] bg-[#F1F5F9]/95 px-6 py-4 backdrop-blur">
          <div className="mx-auto flex max-w-2xl items-center gap-4">
            <button onClick={() => nav("/dashboard")} data-testid="back-btn"
              className="flex items-center gap-1.5 text-sm text-[#1E293B]/70 transition-colors hover:text-[#2563EB]">
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Sections
            </button>
          </div>
        </header>
        <main className="mx-auto max-w-2xl px-6 py-10">
          <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB]">Section {test.section} · {test.code}</div>
          <h1 className="mt-2 font-display text-3xl text-[#1E293B] sm:text-4xl">{test.title}</h1>
          <p className="mt-2 text-sm text-[#1E293B]/60">Please review the notice below before you begin.</p>

          <div className="mt-6 rounded-xl border border-[#E2E8F0] bg-white p-6" data-testid="disclosure-text">
            <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1E293B]/50">
              <ShieldCheck className="h-4 w-4" strokeWidth={1.5} /> Copyright & Disclosure
            </div>
            <p className="whitespace-pre-line text-sm leading-relaxed text-[#1E293B]/80">{DISCLOSURE}</p>
          </div>

          <button
            onClick={() => setAcknowledged(true)}
            data-testid="acknowledge-btn"
            className="mt-6 w-full rounded-full bg-[#2563EB] px-6 py-4 font-semibold text-white transition-transform duration-200 ease-out hover:-translate-y-1"
          >
            I understand — begin {test.code}
          </button>
          <button
            onClick={() => setShowRefs(true)}
            data-testid="references-btn"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-[#1E293B]/20 px-6 py-3 text-sm font-medium text-[#1E293B] transition-transform duration-200 ease-out hover:-translate-y-0.5"
          >
            <BookOpen className="h-4 w-4" strokeWidth={1.5} /> View references
          </button>
        </main>
        {showRefs && <ReferencesModal testId={testId} onClose={() => setShowRefs(false)} />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <header className="sticky top-0 z-10 border-b border-[#E2E8F0] bg-[#F1F5F9]/95 px-6 py-4 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-4">
          <button onClick={() => nav("/dashboard")} data-testid="back-btn"
            className="flex items-center gap-1.5 text-sm text-[#1E293B]/70 transition-colors hover:text-[#2563EB]">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Sections
          </button>
          <div className="flex-1">
            <div className="flex items-center justify-between text-xs text-[#1E293B]/50">
              <span className="uppercase tracking-[0.15em]">{phase} · {test.code}</span>
              <span>{answered}/{total}</span>
            </div>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-[#E2E8F0]">
              <div className="h-full rounded-full bg-[#2563EB] transition-[width] duration-300" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-10">
        <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB]">Section {test.section}</div>
        <h1 className="mt-2 font-display text-3xl text-[#1E293B] sm:text-4xl">{test.title}</h1>
        <p className="mt-2 text-sm text-[#1E293B]/60">{test.subtitle}</p>
        <p className="mt-4 rounded-xl border border-[#E2E8F0] bg-white p-4 text-sm leading-relaxed text-[#1E293B]/75">
          {test.description} Rate each statement: <strong>1 = Strongly Disagree</strong> to <strong>5 = Strongly Agree</strong>.
        </p>
        <button
          onClick={() => setShowRefs(true)}
          data-testid="references-btn"
          className="mt-3 flex items-center gap-2 rounded-full border border-[#1E293B]/20 px-4 py-2 text-sm font-medium text-[#1E293B] transition-transform duration-200 ease-out hover:-translate-y-0.5"
        >
          <BookOpen className="h-4 w-4" strokeWidth={1.5} /> View references
        </button>

        <div className="mt-8 space-y-4">
          {test.questions.map((q, i) => (
            <LikertRow key={q.n} q={q} scale={scale} value={answers[q.n]} onChange={setAnswer} index={i} />
          ))}
        </div>

        <button
          onClick={submit}
          disabled={submitting}
          data-testid="submit-test-btn"
          className="mt-8 w-full rounded-full bg-[#2563EB] px-6 py-4 font-semibold text-white transition-transform duration-200 ease-out hover:-translate-y-1 disabled:opacity-60"
        >
          {submitting ? "Saving…" : `Submit ${test.code} (${phase})`}
        </button>
      </main>
      {showRefs && <ReferencesModal testId={testId} onClose={() => setShowRefs(false)} />}
    </div>
  );
}
